import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronDown } from "react-icons/fa";

const faqData = [
  {
    category: "Clocking In & Out",
    questions: [
      {
        question: "How do I track remote staff hours accurately?",
        answer: "ClockInMaster provides multiple ways to track remote staff hours:",
        points: ["Use our mobile app with GPS tracking", "Set up virtual clock-in boundaries", "Enable automatic alerts for clock-in/out times"],
      },
      {
        question: "How does the clock-in system work?",
        answer: "Employees can clock in and out using their unique PIN or Face ID. The system tracks their working hours, breaks, and end times in real-time. All data is automatically recorded and stored for reporting purposes.",
      },
      {
        question: "What happens if an employee forgets to clock in/out?",
        answer: "Managers can manually adjust time entries if needed. The system maintains an audit trail of all manual adjustments for transparency and compliance purposes.",
      },
    ],
  },
  {
    category: "Reports & Analytics",
    questions: [
      {
        question: "What types of reports are available?",
        answer: "ClockInMaster offers comprehensive reporting options:",
        points: ["Daily Reports - Detailed attendance logs for each day", "Aggregated Reports - Summarized data for any custom date range", "15-Day Instant Reports - Quick access to the last 15 days of attendance data"],
      },
      {
        question: "How can I access employee attendance history?",
        answer: "You can view individual employee attendance records through the daily reports section. The system maintains a complete history of all clock-ins, clock-outs, and breaks for each employee.",
      },
    ],
  },
  {
    category: "Staff Rota & Scheduling",
    questions: [
      {
        question: "How does the rota system work?",
        answer: "The rota system allows you to create and manage employee schedules based on their availability. You can assign shifts, track attendance in real-time, and ensure optimal staffing levels at all times.",
      },
      {
        question: "How do I create weekly rotas?",
        answer: "You can set up rotas on a weekly basis with flexible scheduling options:",
        points: ["Set staff schedules for any day of the week", "Define custom start and end times for each shift", "Copy rota from previous week to save time", "Copy rota to multiple consecutive weeks"],
      },
    ],
  },
  {
    category: "Security & Authentication",
    questions: [
      {
        question: "How secure is the clock-in system?",
        answer: "The system uses multi-factor authentication including unique PINs and Face ID to prevent unauthorized access and buddy punching. All authentication attempts are logged for security purposes.",
      },
      {
        question: "How do I set up Face ID for employees?",
        answer: "Managers can register employee faces through the system. The process involves capturing multiple angles of the employee's face to ensure accurate recognition. Face ID data is securely stored and encrypted.",
      },
    ],
  },
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const toggleQuestion = (index) => setOpenIndex(openIndex === index ? null : index);

  return (
    <div className="content-page">
      {/* Hero Banner */}
      <div className="content-page-header">
        <div className="absolute inset-0 bg-hero-mesh" />
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-extrabold mb-3"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Frequently Asked Questions
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-white/60 text-sm"
          >
            Quick answers to your questions about ClockInMaster
          </motion.p>
        </div>
      </div>

      {/* Content */}
      <div className="content-page-body pb-16">
        <div className="space-y-6">
          {faqData.map((category, categoryIndex) => (
            <motion.div
              key={categoryIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: categoryIndex * 0.08 }}
              className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden"
            >
              <div className="px-6 pt-6 pb-2">
                <h2 className="text-lg font-bold text-gray-900">{category.category}</h2>
              </div>

              <div className="divide-y divide-gray-100">
                {category.questions.map((item, questionIndex) => {
                  const index = categoryIndex * 10 + questionIndex;
                  const isOpen = openIndex === index;
                  return (
                    <div key={index}>
                      <button
                        onClick={() => toggleQuestion(index)}
                        className="w-full text-left px-6 py-4 flex justify-between items-center gap-4 hover:bg-gray-50/50 transition-colors duration-200"
                      >
                        <h3 className="text-sm font-semibold text-gray-700">{item.question}</h3>
                        <motion.div
                          animate={{ rotate: isOpen ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                          className="flex-shrink-0"
                        >
                          <FaChevronDown className="text-xs text-muted" />
                        </motion.div>
                      </button>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="overflow-hidden"
                          >
                            <div className="px-6 pb-5">
                              <p className="text-sm text-gray-600 leading-relaxed">{item.answer}</p>
                              {item.points && (
                                <ul className="mt-3 space-y-2">
                                  {item.points.map((point, pi) => (
                                    <li key={pi} className="flex items-start gap-2.5 text-sm text-gray-600">
                                      <span className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 flex-shrink-0" />
                                      {point}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-gradient-to-br from-primary to-secondary rounded-2xl p-8 text-center mt-8 text-white"
        >
          <h2 className="text-2xl font-bold mb-3">Still have questions?</h2>
          <p className="text-white/70 text-sm mb-5 max-w-md mx-auto">
            Our support team is here to help you with any questions about ClockInMaster.
          </p>
          <Link
            to="/support"
            className="inline-flex items-center gap-2 bg-white text-primary px-6 py-3 rounded-xl font-semibold text-sm hover:shadow-lg transition-all duration-300 hover:scale-105"
          >
            Contact Support
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQ;